import { Box, styled } from '@mui/material';
import Loader from 'Components/Forms/Loader';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { InView } from 'react-intersection-observer';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import {
  ACTIONS_COLUMN_WIDTH_ROW,
  SCROLL_POSITION_THRESHOLD,
  SCROLL_SHADOW_HEIGHT,
  SCROLLBAR_SPACING,
  SELECTION_COLUMN_WIDTH,
  TABLE_HEADER_HEIGHT,
  TABLE_HORIZONTAL_PADDING,
} from './table.constants';
import { IOrderBy, TBaseTableTypes, TTableColumn, TTableRow } from './table.types';
import TableHeader from './TableHeader';
import TableRow from './TableRow';

interface ITableProps<TColumKey extends string | TBaseTableTypes = string> {
  'data-testid'?: string;
  columns: TTableColumn<TColumKey>[];

  /**
   * Columns to hide
   * This adjusts the width calculations accordingly
   */
  hideTableColumns?: TColumKey[];
  hideHeader?: boolean;
  /**
   * Spacing between the scrollbar and rows on the right
   * @default false: Enabled to avoid the scrollbar overlapping the rows
   */
  noScrollbarSpacing?: boolean;
  /**
   * Searchbar will be rendered by default on the first header column
   */
  hideSearchbar?: boolean;
  searchTerm?: string | null;
  searchPlaceholder?: string;
  selectAllChecked?: boolean;
  disableSelectAll?: boolean;
  maxHeight?: number | string;
  // If ordering should be applied to the a column
  orderBy?: IOrderBy<TColumKey>;
  /**
   * Will enable infinite scroll and call onEndReached when the end of the list is reached
   */
  hasMoreRows?: boolean;
  children: (args: { TableRow: TTableRow<TColumKey> }) => JSX.Element | JSX.Element[];
  onSearchTermChange?: (value: string) => void;
  /**
   * Only called when a sortable column header is clicked
   */
  onOrderByChange?: (newOrderBy?: IOrderBy<TColumKey>) => void;
  onSelectionAllClick?: () => void;
  onEndReached?: () => void;
}

const StyledTable = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  justifyContent: 'space-evenly',
}));

const ScrollShadow = styled('div')<{
  direction?: 'top-to-bottom' | 'bottom-to-top';
}>(({ theme, direction = 'top-to-bottom' }) => ({
  position: 'absolute',
  ...(direction === 'top-to-bottom'
    ? { top: SCROLL_SHADOW_HEIGHT }
    : { bottom: -SCROLL_SHADOW_HEIGHT * 2 }),
  left: 0,
  width: '100%',
  height: theme.spacing(SCROLL_SHADOW_HEIGHT),
  background: `linear-gradient(${
    direction === 'top-to-bottom' ? 0 : 180
  }deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 1) 80%)`,
  zIndex: 3,
  pointerEvents: 'none',
  transition: 'opacity 0.1s ease-out',
  '&.hidden': {
    opacity: 0,
  },
}));

const StyledTableBody = styled(Box)<{
  'add-scrollbar-padding': Tbooleanish;
  'top-spacing': Tbooleanish;
}>(({ theme, 'add-scrollbar-padding': addScrollbarPadding, 'top-spacing': topSpacing }) => ({
  width: `calc(100% + ${TABLE_HORIZONTAL_PADDING}px - ${
    addScrollbarPadding === 'true' ? SCROLLBAR_SPACING : 0
  }px )`,
  height: `calc(100% - ${TABLE_HEADER_HEIGHT}px)`,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',

  // Apply gap not for the subtable rows
  '& >:nth-of-type(odd):not(:last-child)': {
    marginBottom: theme.spacing(1),
  },

  // Box shadow of the rows should be visible and not hidden
  marginRight: theme.spacing(-2),
  marginLeft: theme.spacing(-2),
  // -2 margin top is only applied when the header is hidden, otherwise it will be 1 to avoid the header overlapping while scrolling
  marginTop: topSpacing === 'true' ? theme.spacing(1) : theme.spacing(-2),
  marginBottom: theme.spacing(-2),
  // paddingRight: theme.spacing(2 + (addScrollbarPadding === 'true' ? 1 : 0)),
  paddingLeft: theme.spacing(2),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),

  overflowY: 'auto',
  overflowX: 'hidden',

  // Prevent browser from auto-adjusting scroll position when new content is added and users sticks to the bottom of the scroll container
  overflowAnchor: 'none',
}));

const LoadingContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  height: 60,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const Table = <TColumKey extends string | TBaseTableTypes = string>({
  'data-testid': dataTestId = 'custom',
  columns,
  hideTableColumns,
  searchPlaceholder = 'Search',
  searchTerm,
  selectAllChecked,
  noScrollbarSpacing = false,
  hasMoreRows = false,
  maxHeight,
  disableSelectAll,
  hideHeader,
  hideSearchbar,
  orderBy,
  children,
  onOrderByChange,
  onSearchTermChange,
  onSelectionAllClick,
  onEndReached,
}: ITableProps<TColumKey>) => {
  const tableBodyRef = useRef<HTMLDivElement>(null);
  const topShadowRef = useRef<HTMLDivElement>(null);
  const bottomShadowRef = useRef<HTMLDivElement>(null);

  const handleEndReached = (inView: boolean) => {
    if (inView && hasMoreRows) {
      onEndReached?.();
    }
  };

  const _columns = useMemo(() => {
    const amountNormalColumns = columns.filter(
      ({ columnKey, doubleWidth }) =>
        columnKey !== 'selection' &&
        columnKey !== 'actions' &&
        columnKey !== 'collapsable' &&
        !doubleWidth &&
        (!hideTableColumns || !hideTableColumns.includes(columnKey as TColumKey))
    ).length;
    const amountDoubleColumns = columns.filter(
      ({ columnKey, doubleWidth }) =>
        columnKey !== 'selection' &&
        columnKey !== 'actions' &&
        columnKey !== 'collapsable' &&
        doubleWidth &&
        (!hideTableColumns || !hideTableColumns.includes(columnKey as TColumKey))
    ).length;
    const hasSelectionColumn = columns.some(({ columnKey }) => columnKey === 'selection');
    const hasActionsColumn = columns.some(({ columnKey }) => columnKey === 'actions');
    // Calculate total units: each normal column = 1 unit, each double column = 2 units
    const totalUnits = amountNormalColumns + amountDoubleColumns * 2;

    // Calculate fixed width in pixels
    const fixedWidthPx =
      (hasSelectionColumn ? SELECTION_COLUMN_WIDTH : 0) +
      (hasActionsColumn ? ACTIONS_COLUMN_WIDTH_ROW : 0);

    return columns.map(col => {
      if (col.columnKey === 'selection') {
        return { width: SELECTION_COLUMN_WIDTH, ...col };
      }
      if (col.columnKey === 'actions') {
        return { width: ACTIONS_COLUMN_WIDTH_ROW, ...col };
      }

      // Each unit gets an equal share of the remaining width after subtracting fixed columns
      // Normal columns get 1 unit, double-width columns get 2 units
      const unitMultiplier = col.doubleWidth ? 2 : 1;

      return {
        width: `calc((100% - ${fixedWidthPx}px) / ${totalUnits} * ${unitMultiplier})`,
        hidden: hideTableColumns?.includes(col.columnKey as TColumKey) ?? false,
        ...col,
      };
    });
  }, [columns, hideTableColumns]);

  /**
   * Scroll shadow visibility handler using direct DOM manipulation.
   *
   * WHY THIS APPROACH:
   * This implementation intentionally bypasses React's state management for performance reasons.
   * Using React state (useState + setState) to control shadow visibility causes the entire component
   * to re-render on every scroll event, creating visible lag and poor UX.
   *
   * HOW IT WORKS:
   * 1. Shadow elements are always rendered in the DOM (not conditionally)
   * 2. We use refs to get direct references to the shadow DOM elements
   * 3. On scroll events, we directly add/remove CSS classes ('hidden') to control visibility
   * 4. This bypasses React's reconciliation and render cycle entirely
   * 5. Native addEventListener with passive:true ensures optimal scroll performance
   * 6. ResizeObserver recalculates shadows when content changes (rows added/removed)
   *
   * TRADE-OFFS:
   * - Breaks React's declarative paradigm (imperative DOM manipulation)
   * - Shadow visibility state is not reflected in React state
   * - However, provides instant visual feedback without re-renders
   * - Acceptable for pure visual effects that don't affect data or app logic
   */
  const updateShadowVisibility = useCallback(() => {
    const tableBodyElement = tableBodyRef.current;
    const topShadow = topShadowRef.current;
    const bottomShadow = bottomShadowRef.current;

    if (!tableBodyElement || !topShadow || !bottomShadow) return;

    const scrollTop = tableBodyElement.scrollTop;
    const scrollHeight = tableBodyElement.scrollHeight;
    const clientHeight = tableBodyElement.clientHeight;

    // Directly manipulate DOM classes for instant visual feedback
    if (scrollTop <= SCROLL_POSITION_THRESHOLD) {
      topShadow.classList.add('hidden');
    } else {
      topShadow.classList.remove('hidden');
    }

    if (scrollTop + clientHeight >= scrollHeight - SCROLL_POSITION_THRESHOLD) {
      bottomShadow.classList.add('hidden');
    } else {
      bottomShadow.classList.remove('hidden');
    }
  }, []);

  useEffect(() => {
    const tableBodyElement = tableBodyRef.current;

    if (!tableBodyElement) return;

    // Use native addEventListener for immediate response during scrolling
    tableBodyElement.addEventListener('scroll', updateShadowVisibility, { passive: true });

    // ResizeObserver to recalculate shadows when content changes (rows added/removed)
    const resizeObserver = new ResizeObserver(() => {
      updateShadowVisibility();
    });
    resizeObserver.observe(tableBodyElement);

    // Initial check
    updateShadowVisibility();

    return () => {
      tableBodyElement.removeEventListener('scroll', updateShadowVisibility);
      resizeObserver.disconnect();
    };
  }, [updateShadowVisibility]);

  return (
    <StyledTable maxHeight={maxHeight} data-testid={`${dataTestId}-table`}>
      {/* Header row */}
      {!hideHeader && (
        <TableHeader<TColumKey>
          columns={_columns}
          data-testid={`${dataTestId}-table-header`}
          searchPlaceholder={searchPlaceholder}
          searchTerm={searchTerm}
          hideSearchbar={hideSearchbar}
          orderBy={orderBy}
          selectAllChecked={selectAllChecked}
          disableSelectAll={disableSelectAll}
          addScrollbarPadding={!noScrollbarSpacing}
          onSearchTermChange={onSearchTermChange}
          onSelectionAllClick={onSelectionAllClick}
          onOrderByChange={onOrderByChange}
        />
      )}

      <Box
        position="relative"
        width={`calc(100% - 12px - ${!noScrollbarSpacing ? SCROLLBAR_SPACING : 0}px )`}
      >
        <ScrollShadow ref={topShadowRef} direction="top-to-bottom" className="hidden" />
      </Box>

      <StyledTableBody
        ref={tableBodyRef}
        data-testid={`${dataTestId}-table-body`}
        top-spacing={Booleanish(!hideHeader)}
        add-scrollbar-padding={Booleanish(!noScrollbarSpacing)}
      >
        {children({
          TableRow: props => (
            <TableRow<TColumKey>
              data-testid={`${dataTestId}-table-row`}
              columns={_columns}
              {...props}
            />
          ),
        })}

        {hasMoreRows && (
          <InView
            trackVisibility
            as="div"
            delay={200}
            threshold={0.8}
            initialInView
            onChange={handleEndReached}
          >
            <LoadingContainer>
              <Loader size={20} />
            </LoadingContainer>
          </InView>
        )}
      </StyledTableBody>

      <Box
        position="relative"
        width={`calc(100% - 12px - ${!noScrollbarSpacing ? SCROLLBAR_SPACING : 0}px )`}
      >
        <ScrollShadow ref={bottomShadowRef} direction="bottom-to-top" />
      </Box>
    </StyledTable>
  );
};

export default Table;
